import {useCallback} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {MobileViewerSeekbar} from '../../../../../../design-system/organisms/MobileViewerSeekbar/MobileViewerSeekbar';
import {PcViewerSeekbar} from '../../../../../../design-system/organisms/PcViewerSeekbar/PcViewerSeekbar';
import {ViewerType} from '../constant/viewerConstant';
import {
  setCurrentPage,
  setIsHandlingSeekBar,
  setIsProgressBarExpanded,
} from '../state/imageViewerSlice';
import {ImageViewerReadType} from '../type/ImageViewerReadType';
import {calcPageByProgress} from '../util/calcPageByProgress';

export function ImageViewerSeekbarContainer({productId}: {productId: number}) {
  const {isMobile} = requestContext.requestInfo.display;

  const {
    currentPage,
    totalPage,
    readType,
    isProgressBarExpanded,
    loadedProgress,
    isTwoPageLayout,
  } = useTypedSelector(state => ({
    currentPage: state.imageViewer.viewData[productId]?.currentPage || 1,
    totalPage: state.imageViewer.viewData[productId]?.totalPage || 1,
    readType: state.imageViewer.settings.readType,
    isProgressBarExpanded:
      state.imageViewer.viewData[productId]?.isProgressBarExpanded,
    loadedProgress: state.imageViewer.viewData[productId]?.loadedProgress,
    isTwoPageLayout: state.imageViewer.settings.isTwoPageLayout,
  }));

  const dispatch = useTypedDispatch();

  const onMouseEnter = useCallback(() => {
    dispatch(
      setIsProgressBarExpanded({productId, isProgressBarExpanded: true})
    );
  }, [dispatch, productId]);

  const onMouseLeave = useCallback(() => {
    dispatch(
      setIsProgressBarExpanded({productId, isProgressBarExpanded: false})
    );
  }, [dispatch, productId]);

  // 인디케이터 누른 상태로 이동중에는 실제 페이지를 변경하지 않음
  const onChangeProgress = useCallback(
    (newProgress: number) => {
      dispatch(setIsHandlingSeekBar({productId, isHandling: true}));
      const newPage = calcPageByProgress({
        newProgress,
        totalPage,
        isTwoPageLayout:
          isTwoPageLayout && readType === ImageViewerReadType.Page,
      });
      dispatch(setCurrentPage({productId, page: newPage, isBySeekbar: true}));
    },
    [dispatch, isTwoPageLayout, productId, readType, totalPage]
  );

  const onReleaseProgressBarMobile = useCallback(() => {
    dispatch(setIsHandlingSeekBar({productId, isHandling: false}));
  }, [dispatch, productId]);

  // PC의 경우 addEventListener로 mouseDown시 함수를 넣어주므로 up 시에는 progress를 재계산 해야합니다
  const onReleaseProgressBarPc = useCallback(
    (newProgress: number) => {
      dispatch(setIsHandlingSeekBar({productId, isHandling: false}));
      const newPage = calcPageByProgress({
        newProgress,
        totalPage,
        isTwoPageLayout:
          isTwoPageLayout && readType === ImageViewerReadType.Page,
      });
      dispatch(setCurrentPage({productId, page: newPage, isBySeekbar: true}));
    },
    [totalPage, isTwoPageLayout, readType, dispatch, productId]
  );

  const onPressProgressBar = useCallback(() => {
    dispatch(setIsHandlingSeekBar({productId, isHandling: true}));
    dispatch(
      setIsProgressBarExpanded({productId, isProgressBarExpanded: true})
    );
  }, [dispatch, productId]);

  return isMobile ? (
    <MobileViewerSeekbar
      viewerType={ViewerType.ImageViewer}
      loadedProgress={loadedProgress}
      currentProgress={(currentPage - 1) / (totalPage - 1)}
      totalPage={totalPage}
      currentPage={Math.min(currentPage, totalPage)}
      loadedPage={totalPage}
      onChangeProgress={onChangeProgress}
      onPressProgressBar={onPressProgressBar}
      onReleaseProgressBar={onReleaseProgressBarMobile}
    />
  ) : (
    <PcViewerSeekbar
      loadedProgress={loadedProgress}
      currentProgress={(currentPage - 1) / (totalPage - 1)}
      totalPage={totalPage}
      currentPage={Math.min(currentPage, totalPage)}
      isProgressBarExpanded={isProgressBarExpanded}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onChangeProgress={onChangeProgress}
      onPressProgressBar={onPressProgressBar}
      onReleaseProgressBar={onReleaseProgressBarPc}
    />
  );
}
